import config from '../config';

interface LogEntry {
  level: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  timestamp: string;
  service: string;
  url: string;
  userAgent: string;
  context?: any;
  stack?: string;
  userId?: number;
  userEmail?: string;
}

class FrontendLogger {
  private queue: LogEntry[] = [];
  private isOnline: boolean = navigator.onLine;
  private flushInterval: number = 5000; // 5 seconds
  private timer: NodeJS.Timeout | null = null;
  private maxQueueSize: number = 100;
  private minLevel: string = process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
  private skipLevels: string[] = ['debug', 'info']; // Don't send these to backend

  constructor() {
    this.setupEventListeners();
    this.startFlushTimer();
    this.setupGlobalErrorHandler();
  }

  private setupEventListeners() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flush();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Listen for page unload to flush logs
    window.addEventListener('beforeunload', () => {
      this.flush(true);
    });
  }

  private setupGlobalErrorHandler() {
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.error('Unhandled error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise,
      });
    });
  }

  private startFlushTimer() {
    this.timer = setInterval(() => {
      if (this.queue.length > 0 && this.isOnline) {
        this.flush();
      }
    }, this.flushInterval);
  }

  private getLogLevel(level: string): number {
    const levels: Record<string, number> = {
      error: 0,
      warn: 1,
      info: 2,
      debug: 3,
    };
    return levels[level] || 3;
  }

  private shouldLog(level: string): boolean {
    return this.getLogLevel(level) <= this.getLogLevel(this.minLevel);
  }

  private createLogEntry(
    level: 'error' | 'warn' | 'info' | 'debug',
    message: string,
    context?: any
  ): LogEntry {
    const authUser = sessionStorage.getItem('authUser');
    const user = authUser ? JSON.parse(authUser) : null;

    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      service: 'frontend',
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: user?.data?.id,
      userEmail: user?.data?.email,
      context,
      stack: context?.error?.stack || new Error().stack,
    };
  }

  private addToQueue(entry: LogEntry) {
    // Skip certain log levels from being sent to backend
    if (this.skipLevels.includes(entry.level)) {
      return;
    }

    this.queue.push(entry);

    // Limit queue size
    if (this.queue.length > this.maxQueueSize) {
      this.queue = this.queue.slice(-this.maxQueueSize);
    }

    // Flush immediately for errors in production
    if (entry.level === 'error' && process.env.NODE_ENV === 'production') {
      this.flush();
    }
  }

  async flush(force: boolean = false) {
    if (this.queue.length === 0) return;
    if (!this.isOnline && !force) return;

    // Skip flushing if no authentication token is available
    const authUser = sessionStorage.getItem('authUser');
    const token = authUser ? JSON.parse(authUser).token : null;
    
    if (!token) {
      // Don't try to send logs without authentication
      // Keep logs in queue for when user logs in
      return;
    }

    const logsToSend = [...this.queue];
    this.queue = [];

    try {
      const response = await fetch(`${config.api.API_URL}/logs/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ logs: logsToSend }),
      });

      if (!response.ok) {
        // Re-add logs to queue if sending failed
        this.queue = [...logsToSend, ...this.queue];
      }
    } catch (error) {
      // Re-add logs to queue if sending failed
      this.queue = [...logsToSend, ...this.queue];
      
      // Use console for meta-logging errors
      console.error('Failed to send logs to server:', error);
    }
  }

  error(message: string, context?: any) {
    if (!this.shouldLog('error')) return;
    
    // Enhance error context with all available details
    const enhancedContext = {
      ...context,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    // If context contains an error object, extract all details
    if (context?.error) {
      enhancedContext.errorMessage = context.error.message;
      enhancedContext.errorStack = context.error.stack;
      enhancedContext.errorName = context.error.name;
      
      // For network errors
      if (context.error.response) {
        enhancedContext.responseStatus = context.error.response.status;
        enhancedContext.responseData = context.error.response.data;
      }
    }
    
    console.error(message, enhancedContext);
    const entry = this.createLogEntry('error', message, enhancedContext);
    this.addToQueue(entry);
  }

  warn(message: string, context?: any) {
    if (!this.shouldLog('warn')) return;
    
    console.warn(message, context);
    const entry = this.createLogEntry('warn', message, context);
    this.addToQueue(entry);
  }

  info(message: string, context?: any) {
    if (!this.shouldLog('info')) return;
    
    console.info(message, context);
    const entry = this.createLogEntry('info', message, context);
    this.addToQueue(entry);
  }

  debug(message: string, context?: any) {
    if (!this.shouldLog('debug')) return;
    
    console.debug(message, context);
    const entry = this.createLogEntry('debug', message, context);
    this.addToQueue(entry);
  }

  // Track user actions
  logAction(action: string, details?: any) {
    this.info(`User action: ${action}`, { action, details });
  }

  // Track API calls
  logApiCall(method: string, url: string, status: number, duration: number, error?: any) {
    const level = status >= 400 ? 'error' : 'info';
    const message = `API ${method} ${url} - ${status} (${duration}ms)`;
    
    const logMethod = level === 'error' ? this.error : this.info;
    logMethod.call(this, message, {
      method,
      url,
      status,
      duration,
      error,
    });
  }

  // Track performance metrics
  logPerformance(metric: string, value: number, context?: any) {
    this.info(`Performance: ${metric}`, {
      metric,
      value,
      ...context,
    });
  }

  // Manual flush
  forceFlush() {
    return this.flush(true);
  }

  // Clean up
  destroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.flush(true);
  }
}

// Create singleton instance
const frontendLogger = new FrontendLogger();

// Export for use in React components
export default frontendLogger;

// Export convenience methods
export const logError = frontendLogger.error.bind(frontendLogger);
export const logWarn = frontendLogger.warn.bind(frontendLogger);
export const logInfo = frontendLogger.info.bind(frontendLogger);
export const logDebug = frontendLogger.debug.bind(frontendLogger);
export const logAction = frontendLogger.logAction.bind(frontendLogger);
export const logApiCall = frontendLogger.logApiCall.bind(frontendLogger);
export const logPerformance = frontendLogger.logPerformance.bind(frontendLogger);